import type { LifecycleStateLabel, ProjectRun } from "@/api/projects";
import type { FlowRunFeedSummary } from "@/api/runs";

/**
 * Pure grouping for the Runs tab (flowstack#1799): the flat `/api/runs` rows
 * become one entry per thing a user SUBMITTED — a flow run, with its steps
 * nested, or a standalone run.
 *
 * Self-contained on purpose (no store, no query, no router), so it can move to
 * the flowstack-ui library with the component that renders it.
 */

/** One flow submission and the steps of it that the feed returned. */
export interface FlowRunGroup {
  kind: "flow";
  flowRunId: string;
  /** The server's rollup, or one derived from `steps` when it sent none. */
  summary: FlowRunFeedSummary;
  /** `true` when `summary` was derived from the rows in the window only: an
   * older server that sends no rollup. The counts then cover only `steps`. */
  derived: boolean;
  /** Oldest first — the order the runner dispatched them in. */
  steps: ProjectRun[];
  /** The newest step's `submitted_at`: where the group sorts in the feed. */
  latestAt: string;
}

/** A run that is not a step of a known flow run. */
export interface StandaloneRunEntry {
  kind: "run";
  run: ProjectRun;
}

export type RunFeedEntry = FlowRunGroup | StandaloneRunEntry;

const TERMINAL: ReadonlySet<LifecycleStateLabel> = new Set([
  "completed",
  "failed_validation",
  "failed_system",
  "failed_contract",
  "failed_scientific",
]);

export function isFailedState(state: LifecycleStateLabel | null | undefined): boolean {
  return state != null && state.startsWith("failed_");
}

function byTime(a: string, b: string): number {
  return Date.parse(a) - Date.parse(b);
}

/** A rollup from the window's rows alone, for a server that sends none. */
function deriveSummary(flowRunId: string, steps: ProjectRun[]): FlowRunFeedSummary {
  const failed = steps.filter((s) => isFailedState(s.status));
  const first = failed[0];
  return {
    flow_run_id: flowRunId,
    flow_name: steps[0]?.flow_name ?? null,
    status: null,
    steps_total: steps.length,
    steps_settled: steps.filter((s) => TERMINAL.has(s.status)).length,
    steps_failed: failed.length,
    submitted_at: steps[0]?.submitted_at ?? "",
    finished_at: null,
    failing_step: first
      ? { node_name: first.flow_node_name ?? null, run_id: first.run_id, status: first.status }
      : null,
  };
}

/**
 * Group `runs` by `flow_run_id`, newest entry first.
 *
 * A row with no `flow_run_id` stays a standalone entry — including a flow
 * child written before the column existed, which cannot be attributed to a
 * submission and is listed as it always was rather than guessed into one.
 */
export function groupRunFeed(runs: ProjectRun[], flowRuns: FlowRunFeedSummary[]): RunFeedEntry[] {
  const summaries = new Map(flowRuns.map((s) => [s.flow_run_id, s]));
  const stepsByFlowRun = new Map<string, ProjectRun[]>();
  const entries: RunFeedEntry[] = [];

  for (const run of runs) {
    const flowRunId = run.flow_run_id;
    if (!flowRunId) {
      entries.push({ kind: "run", run });
      continue;
    }
    const steps = stepsByFlowRun.get(flowRunId);
    if (steps) steps.push(run);
    else stepsByFlowRun.set(flowRunId, [run]);
  }

  for (const [flowRunId, unsorted] of stepsByFlowRun) {
    const steps = [...unsorted].sort(
      (a, b) => byTime(a.submitted_at, b.submitted_at) || a.run_id.localeCompare(b.run_id)
    );
    const summary = summaries.get(flowRunId);
    const resolved = summary ?? deriveSummary(flowRunId, steps);
    entries.push({
      kind: "flow",
      flowRunId,
      summary: resolved,
      derived: summary === undefined,
      steps,
      latestAt: steps[steps.length - 1]?.submitted_at ?? resolved.submitted_at,
    });
  }

  const at = (e: RunFeedEntry): string => (e.kind === "flow" ? e.latestAt : e.run.submitted_at);
  return entries.sort((a, b) => byTime(at(b), at(a)));
}

function stepMatches(run: ProjectRun, needle: string, state: string): boolean {
  if (state && run.status !== state) return false;
  if (!needle) return true;
  return [
    run.run_id,
    run.tile_name ?? "",
    run.flow_name ?? "",
    run.flow_node_name ?? "",
    run.flow_run_id ?? "",
    run.submitted_by,
    run.project_slug ?? "",
  ]
    .join(" ")
    .toLowerCase()
    .includes(needle);
}

/**
 * Apply the Runs tab's search box and state filter to grouped entries.
 *
 * Both filters act on RUNS, as they did on the flat list: a flow run is kept
 * when at least one of its steps matches, and keeps only its matching steps.
 * Its summary is untouched — it describes the whole flow run, not the slice
 * the filter left visible.
 */
export function filterRunFeed(entries: RunFeedEntry[], q: string, state: string): RunFeedEntry[] {
  const needle = q.trim().toLowerCase();
  if (!needle && !state) return entries;
  const out: RunFeedEntry[] = [];
  for (const entry of entries) {
    if (entry.kind === "run") {
      if (stepMatches(entry.run, needle, state)) out.push(entry);
      continue;
    }
    const steps = entry.steps.filter((s) => stepMatches(s, needle, state));
    if (steps.length > 0) out.push({ ...entry, steps });
  }
  return out;
}

/** How many runs (rows) the entries hold — what the "N of M runs" count says. */
export function countRuns(entries: RunFeedEntry[]): number {
  return entries.reduce((n, e) => n + (e.kind === "flow" ? e.steps.length : 1), 0);
}
